import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Tooltip,
  Divider,
} from "@mui/material";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import Navbar from "../../globals/components/Navbar";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import { apiClient } from "../../globals/utils/api";
import { setLawFirmBondsFilters, setLawFirmBondsPage, resetLawFirmBondsState, setLawFirmPreset } from "../../store/slices/lawFirmBondsSlice";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import NumberFormatCustom from "../../globals/utils/Bond Creation Utils/numberFormatCustom";
import TablePaginationActions from "../../globals/components/TablePaginationActions";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const statusOptions = ["Active", "Rejected", "To Be Cancelled", "Cancelled"];

const ENDORSEMENT_NAME_CHANGE_TYPES = [
  "Name_Change_Principal",
  "Name_Change_County",
  "Name_Change_Deceased_Ward_Beneficiary",
];
const isNameChangeEndorsement = (t) => ENDORSEMENT_NAME_CHANGE_TYPES.includes(t);

const DWB_LABEL_MAP = {
  Probate: { prefix: "Deceased Legal", alias: "Alias (Deceased)" },
  Guardianship: { prefix: "Ward", alias: "Ward Alias" },
  VA: { prefix: "Beneficiary", alias: "Beneficiary Alias" },
  Trustee: { prefix: "Full Legal Name", single: true },
};

const LawFirmApproveBonds = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const lawfirmId = useSelector((state) => state.lawfirm.lawfirmId);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const savedFilters = useSelector((state) => state.lawFirmBonds?.filters) || {
    bondNumber: [],
    bondType: [],
    bondSubCategory: [],
    principal: [],
    estate: [],
    lawFirm: [],
    attorney: [],
    status: [],
    startDate: null,
    endDate: null,
  };
  const savedPage = useSelector((state) => state.lawFirmBonds?.page ?? 0);
  const [filters, setFilters] = useState(savedFilters);

  const [bondTypes, setBondTypes] = useState([]);
  const [lawFirms, setLawFirms] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [, setPrincipals] = useState([]);
  const [allBonds, setAllBonds] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [lawFirmBondAllowList, setLawFirmBondAllowList] = useState([]);
  const [searchQuery, setSearchQuery] = useState(""); // eslint-disable-line no-unused-vars
  const [page, setPage] = useState(savedPage);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedBondToCancel, setSelectedBondToCancel] = useState(null);
  const [cancellationFile, setCancellationFile] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const bondTypeToSubcategories = {
    Probate: [
      "Administrator",
      "Conservator",
      "Curator",
      "Executor",
      "Personal Representative",
    ],
    Guardianship: [
      "Guardian - Incapacitated",
      "Guardian - Minor",
      "Guardian - Advocate",
      "Conservator",
    ],
    Blanket: ["1 Year Bond", "2 Year Bond", "3 Year Bond"],
  };

  const normalizeBondKey = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  };

  const bondMatchesAllowList = (bond, allowSet) => {
    if (!allowSet || allowSet.size === 0) return true;
    return (
      allowSet.has(normalizeBondKey(bond?.bond_number)) ||
      allowSet.has(normalizeBondKey(bond?.bond_id))
    );
  };

  const bondNumberOptions = useMemo(() => {
    const allowSet = new Set(
      Array.isArray(lawFirmBondAllowList)
        ? lawFirmBondAllowList.flatMap((b) => [
            normalizeBondKey(b?.bond_number),
            normalizeBondKey(b?.bond_id),
          ].filter(Boolean))
        : []
    );

    const source = allowSet.size
      ? allBonds.filter((b) => bondMatchesAllowList(b, allowSet))
      : allBonds;

    return [...new Set(source.map((b) => b.bond_number).filter(Boolean))].map(
      (num) => ({ label: num, value: num })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBonds, lawFirmBondAllowList]);

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lawfirmId]);

  useEffect(() => {
    if (lawfirmId && lawFirms.length > 0) {
      const selectedLawFirm = lawFirms.find(firm => firm.value === lawfirmId);
      if (selectedLawFirm) {
        setFilters(prev => ({
          ...prev,
          lawFirm: [selectedLawFirm]
        }));
        dispatch(setLawFirmPreset([selectedLawFirm]));
        const allowSet = new Set(
          Array.isArray(lawFirmBondAllowList)
            ? lawFirmBondAllowList.flatMap((b) => [
                normalizeBondKey(b?.bond_number),
                normalizeBondKey(b?.bond_id),
              ].filter(Boolean))
            : []
        );
        let result = [...allBonds].filter(
          (b) => b.law_firm_name === selectedLawFirm.label
        );
        if (allowSet.size) {
          result = result.filter((b) => bondMatchesAllowList(b, allowSet));
        }
        setFilteredData(result);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lawfirmId, lawFirms, allBonds, dispatch, lawFirmBondAllowList]);

  useEffect(() => {
    if (filters.lawFirm.length > 0) {
      const firmIds = filters.lawFirm.map((f) => f.value);
      Promise.all(
        firmIds.map((id) => apiClient(`/lawfirm-user-location/${id}`, "GET"))
      ).then((responses) => {
        const merged = [].concat(...responses)
          .filter((a) => a.Position === "Attorney")
          .map((a) => ({
            label: a.lawfirm_user_name,
            value: a.lawfirm_user_id,
          }));
        setAttorneys(merged);
      });
    } else {
      setAttorneys([]);
    }
  }, [filters.lawFirm]);

  const fetchInitialData = async (overrideFilters = null) => {
    setLoading(true);
    try {
      const [bt, lf, pr] = await Promise.all([
        apiClient("/bondtypes/", "GET"),
        apiClient("/list-lawfirms/", "GET"),
        apiClient("/principals", "GET"),
      ]);

      const lawFirmsData = lf.map((f) => ({ label: f.name, value: f.lawfirm_id }));
      setLawFirms(lawFirmsData);

      setBondTypes(
        bt
          .filter((b) => b.type !== "Guardian-Minor")
          .map((b) => ({ label: b.type, value: b.type }))
      );
      setPrincipals(pr.map((p, index) => ({ 
        label: p.name, 
        bondType: p.role, 
        value: `${p.name}-${p.role}-${index}` 
      })));

      if (lawfirmId) {
        const selectedLawFirm = lawFirmsData.find(firm => firm.value === lawfirmId);
        if (selectedLawFirm) {
          setFilters(prev => ({
            ...prev,
            lawFirm: [selectedLawFirm]
          }));
          dispatch(setLawFirmPreset([selectedLawFirm]));

          try {
            const lawFirmBondList = await apiClient(
              `/list-lawfirm-bonds/${lawfirmId}/`,
              "GET"
            );
            const safeList = Array.isArray(lawFirmBondList) ? lawFirmBondList : [];
            setLawFirmBondAllowList(safeList);

            const formatted = safeList.map((b) => ({
              bond_id: b.bond_id,
              bond_number: b.bond_number || "",
              bond_type: b.bond_type,
              bond_sub_category: b.bond_sub_category,
              principals: b.principals || [],
              estate_name: b.name,
              law_firm_name: b.law_firm_name,
              attorney_name: b.law_firm_user_name,
              underwriting_status: b.underwriting_status || "-",
              bond_amount: b.bond_amount || "0.00",
              status: b.status === "Approved" ? "Active" : (b.status || "Active"),
              created_at: b.created_at,
            }));

            setAllBonds(formatted);

            const f = overrideFilters ? { ...overrideFilters, lawFirm: [selectedLawFirm] } : (filters?.lawFirm?.length ? filters : { ...filters, lawFirm: [selectedLawFirm] });
            let filtered = [...formatted];

            if (f.bondNumber?.length)
              filtered = filtered.filter((b) => f.bondNumber.some((x) => x.value === b.bond_number));
            if (f.startDate) {
              const start = dayjs(f.startDate).startOf("day");
              filtered = filtered.filter((b) => dayjs(b.created_at).isSameOrAfter(start, "day"));
            }
            if (f.endDate) {
              const end = dayjs(f.endDate).endOf("day");
              filtered = filtered.filter((b) => dayjs(b.created_at).isSameOrBefore(end, "day"));
            }
            if (f.bondType?.length)
              filtered = filtered.filter((b) => f.bondType.some((x) => x.value === b.bond_type));
            if (f.bondSubCategory?.length)
              filtered = filtered.filter((b) => f.bondSubCategory.includes(b.bond_sub_category));
            if (f.principal?.length)
              filtered = filtered.filter((b) => f.principal.some((x) => b.principals.includes(x.label)));
            if (f.estate?.length)
              filtered = filtered.filter((b) => f.estate.some((x) => b.estate_name === x.label));
            if (f.attorney?.length)
              filtered = filtered.filter((b) => f.attorney.some((x) => b.attorney_name === x.label));
            if (f.status?.length)
              filtered = filtered.filter((b) => f.status.includes(b.status));

            setFilteredData(filtered);
          } catch (error) {
            console.error("Error fetching law firm bonds:", error);
            setLawFirmBondAllowList([]);
            setAllBonds([]);
            setFilteredData([]);
          }
        } else {
          setLawFirmBondAllowList([]);
          setAllBonds([]);
          setFilteredData([]);
        }
      } else {
        setLawFirmBondAllowList([]);
        setAllBonds([]);
        setFilteredData([]);
      }
    } catch (err) {
      console.error("Error loading data", err);
    } finally {
      setLoading(false);
    }
  };

  const sortRows = (rows) => {
    return [...rows].sort((a, b) => {
      if (orderBy === "bond_amount") {
        const valA = parseFloat(a.bond_amount);
        const valB = parseFloat(b.bond_amount);
        return order === "asc" ? valA - valB : valB - valA;
      } else if (orderBy === "created_at") {
        return order === "asc"
          ? new Date(a.created_at) - new Date(b.created_at)
          : new Date(b.created_at) - new Date(a.created_at);
      } else {
        const valA = a[orderBy]?.toString().toLowerCase() || "";
        const valB = b[orderBy]?.toString().toLowerCase() || "";
        return order === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
    });
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };


  const applyFilters = () => {
    let result = [...allBonds];

    if (filters.bondNumber.length)
      result = result.filter((b) =>
        filters.bondNumber.some((f) => f.value === b.bond_number)
      );

    if (filters.startDate) {
      const start = dayjs(filters.startDate).startOf("day");
      result = result.filter((b) =>
        dayjs(b.created_at).isSameOrAfter(start, "day")
      );
    }

    if (filters.endDate) {
      const end = dayjs(filters.endDate).endOf("day");
      result = result.filter((b) =>
        dayjs(b.created_at).isSameOrBefore(end, "day")
      );
    }

    if (filters.bondType.length)
      result = result.filter((b) =>
        filters.bondType.some((f) => f.value === b.bond_type)
      );

    if (filters.bondSubCategory.length)
      result = result.filter((b) =>
        filters.bondSubCategory.includes(b.bond_sub_category)
      );

    if (filters.principal.length)
      result = result.filter((b) =>
        filters.principal.some((f) => b.principals.includes(f.label))
      );

    if (filters.estate.length)
      result = result.filter((b) =>
        filters.estate.some((f) => b.estate_name === f.label)
      );

    if (filters.lawFirm.length)
      result = result.filter((b) =>
        filters.lawFirm.some((f) => b.law_firm_name === f.label)
      );

    if (filters.attorney.length)
      result = result.filter((b) =>
        filters.attorney.some((f) => b.attorney_name === f.label)
      );

    if (filters.status.length)
      result = result.filter((b) => filters.status.includes(b.status));

    setFilteredData(result);
    setFilterDialogOpen(false);
    setPage(0);
    // Never remove law firm in stored filters
    const next = { ...filters };
    if (!next.lawFirm?.length && savedFilters?.lawFirm?.length) {
      next.lawFirm = savedFilters.lawFirm;
    }
    dispatch(setLawFirmBondsFilters(next));
    dispatch(setLawFirmBondsPage(0));
  };

  const clearFilters = () => {
    const currentLawFirm = filters.lawFirm;
    const cleared = {
      bondNumber: [],
      bondType: [],
      bondSubCategory: [],
      principal: [],
      estate: [],
      lawFirm: currentLawFirm,
      attorney: [],
      status: [],
      startDate: null,
      endDate: null,
    };
    setFilters(cleared);
    dispatch(setLawFirmBondsFilters(cleared));
    setPage(0);
    dispatch(setLawFirmBondsPage(0));
  };

  const selectedBondTypes = filters.bondType.map((b) => b.value);

  const availableSubcategories = selectedBondTypes.flatMap(
    (type) => bondTypeToSubcategories[type] || []
  );

  // Principal filter options: only principals that appear in the current filtered list (plus any currently selected so the chip displays)
  const principalFilterOptions = useMemo(() => {
    const names = new Set();
    filteredData.forEach((b) => {
      (b.principals || []).forEach((p) => names.add(p));
    });
    (filters.principal || []).forEach((f) => {
      const label = typeof f === "object" && f !== null && "label" in f ? f.label : String(f);
      if (label) names.add(label);
    });
    return [...names].sort().map((name) => ({ label: name, value: name }));
  }, [filteredData, filters.principal]);

  // Estate filter options: only estates that appear in the current filtered list (plus any currently selected so the chip displays), same as principal
  const estateFilterOptions = useMemo(() => {
    const names = new Set();
    filteredData.forEach((b) => {
      if (b.estate_name) names.add(b.estate_name);
    });
    (filters.estate || []).forEach((f) => {
      const label = typeof f === "object" && f !== null && "label" in f ? f.label : String(f);
      if (label) names.add(label);
    });
    return [...names].sort().map((name) => ({ label: name, value: name }));
  }, [filteredData, filters.estate]);

  const filteredRows = sortRows(
    filteredData.filter((r) => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        r.principals.some((p) => p.toLowerCase().includes(query)) ||
        r.bond_number?.toLowerCase().includes(query) ||
        r.estate_name?.toLowerCase().includes(query) ||
        r.law_firm_name?.toLowerCase().includes(query) ||
        r.attorney_name?.toLowerCase().includes(query) ||
        r.status?.toLowerCase().includes(query)
      );
    })
  ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // eslint-disable-next-line no-unused-vars
  const getCancellationInstructions = (bond) => {
    const amount = parseFloat(bond.bond_amount || 0);
    const type = bond.bond_type;

    if (type === "Probate") {
      if (amount <= 250000) {
        return "Probate\n\nBonds up to $250,000\n\n• Discharge from the court\n\n• Statement from attorney on their letterhead stating all debts are paid and balances distributed to the remaining heirs";
      }
      return "Probate\n\nBonds over $250,000 require one of the following to close:\n\n• Final Accounting\n\n• Discharge from the court\n\n• Copy of verification of closing evidence from a court's online system";
    }

    if (type === "Guardianship") {
      const instructions =
        amount <= 1000000
          ? "Guardianship\n\nBonds up to $1,000,000 also accept one of the following to close:\n\n• Final Accounting\n\n• Discharge from the court\n\n• Statement from attorney on their letterhead stating all debts are paid and balances distributed to the remaining heirs and/or beneficiaries"
          : "Guardianship\n\nBonds over $1,000,000 require one of the following to close:\n\n• Final Accounting\n\n• Discharge from the court\n\n• Copy of verification of closing evidence from a court's online system";
      const successorNote =
        "\n\nIf a Successor Bond has been filed with the court:\n\n• Guardianship - Evidence of death of the incompetent\n\n• Minor Guardian - Evidence of the minor reaching the age of majority";
      return instructions + successorNote;
    }

    if (type === "VA") {
      return "VA Bonds\n\nExoneration Letter from the VA";
    }

    if (type === "Trustee") {
      return "Trustee Bond\n\n• Court's Release\n\n• Final Accounting\n\n• Evidence that either the beneficiary of trust has died or that trust assets have been fully dispersed to beneficiary(s) per the Trust";
    }

    if (type === "Blanket") {
      return "📩 Upload email confirmation that renewal is not required.";
    }

    return "⚠️ Upload appropriate closing document for this bond.";
  };

  const getCancellationInstructionsList = (bond) => {
    const amount = parseFloat(bond.bond_amount || 0);
    const type = bond.bond_type;
    if (type === "Probate") {
      if (amount <= 250000) {
        return [
          "Discharge from the court",
          "Statement from attorney on their letterhead stating all debts are paid and balances distributed to the remaining heirs",
        ];
      }
      return [
        "Final Accounting",
        "Discharge from the court",
        "Copy of verification of closing evidence from a court's online system",
      ];
    }
    if (type === "Guardianship") {
      const main =
        amount <= 1000000
          ? [
              "Final Accounting",
              "Discharge from the court",
              "Statement from attorney on their letterhead stating all debts are paid and balances distributed to the remaining heirs and/or beneficiaries",
            ]
          : [
              "Final Accounting",
              "Discharge from the court",
              "Copy of verification of closing evidence from a court's online system",
            ];
      return [
        ...main,
        "If a Successor Bond has been filed with the court:",
        "Guardianship - Evidence of death of the incompetent",
        "Minor Guardian - Evidence of the minor reaching the age of majority",
      ];
    }
    if (type === "VA") return ["Exoneration Letter from the VA"];
    if (type === "Trustee") {
      return [
        "Court's Release",
        "Final Accounting",
        "Evidence that either the beneficiary of trust has died or that trust assets have been fully dispersed to beneficiary(s) per the Trust",
      ];
    }
    if (type === "Blanket") return ["Email confirmation that renewal is not required."];
    return ["Upload appropriate closing document for this bond."];
  };

  const [endorsementDialogOpen, setEndorsementDialogOpen] = useState(false);
  const [selectedBondToEndorse, setSelectedBondToEndorse] = useState(null);
  const [endorsementType, setEndorsementType] = useState("Increase");
  const [newBondAmount, setNewBondAmount] = useState("");
  const [endorsementFile, setEndorsementFile] = useState(null);
  const [isSubmittingEndorsement, setIsSubmittingEndorsement] = useState(false);
  const userdetails = useSelector((state) => state.auth.user);
  const role = useSelector((state) => state.auth.role);
  const [contactName, setContactName] = useState(userdetails.first_name + " " + userdetails.last_name);
  const [contactEmail, setContactEmail] = useState(userdetails.email);
  const [updatedName, setUpdatedName] = useState("");
  const [counties, setCounties] = useState([]);
  const [endorsementToCounty, setEndorsementToCounty] = useState("");
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [estimatedAssets, setEstimatedAssets] = useState("");
  const [dwbFirstName, setDwbFirstName] = useState("");
  const [dwbMiddleName, setDwbMiddleName] = useState("");
  const [dwbLastName, setDwbLastName] = useState("");
  const [dwbSuffix, setDwbSuffix] = useState("");
  const [dwbAlias, setDwbAlias] = useState("");

  // Can't find a bond dialog state
  const [cantFindDialogOpen, setCantFindDialogOpen] = useState(false);
  const [cfBondNumber, setCfBondNumber] = useState("");
  const [cfBondType, setCfBondType] = useState(null);
  const [cfName, setCfName] = useState("");
  const [cfPrincipalName, setCfPrincipalName] = useState("");
  const [cfAction, setCfAction] = useState("cancellation");
  const [cfOtherInfo, setCfOtherInfo] = useState("");
  const [cfSubmitting, setCfSubmitting] = useState(false);

  const getEndorsementInstructions = (bond) => {
    const type = bond.bond_type;

    const typeSupports = {
      courtOrder: ["Probate", "Guardianship", "Trustee"],
      accounting: ["Guardianship", "Trustee"],
      vaLetter: ["VA"],
    };

    return [
      ...(typeSupports.courtOrder.includes(type)
        ? ["• Court Order or Endorsement Checklist"]
        : []),
      ...(typeSupports.accounting.includes(type)
        ? ["• Annual Accounting Documentation"]
        : []),
      ...(typeSupports.vaLetter.includes(type)
        ? ["• VA Letter of Endorsement"]
        : []),
    ];
  };

  const bondStateId = selectedBondToEndorse?.state ?? 6;
  useEffect(() => {
    if (!endorsementDialogOpen || !bondStateId) return;
    const fetchCounties = async () => {
      setLoadingCounties(true);
      try {
        const countyResponse = await apiClient(
          `/counties-by-state/${bondStateId}/`,
          "GET"
        );
        const formatted = (countyResponse || []).map((c) => ({
          label: c.bond_county,
          value: c.bond_county,
        }));
        setCounties(formatted);
      } catch {
        setCounties([]);
      } finally {
        setLoadingCounties(false);
      }
    };
    fetchCounties();
  }, [endorsementDialogOpen, bondStateId]);

  const endorsementDwbLabels =
    selectedBondToEndorse?.bond_type != null
      ? DWB_LABEL_MAP[selectedBondToEndorse.bond_type] || DWB_LABEL_MAP.Probate
      : DWB_LABEL_MAP.Probate;
  const dwbOk =
    endorsementType !== "Name_Change_Deceased_Ward_Beneficiary" ||
    (endorsementDwbLabels.single
      ? !!String(updatedName).trim()
      : !!String(dwbFirstName).trim() && !!String(dwbLastName).trim());
  const countyOk = endorsementType !== "Name_Change_County" || !!endorsementToCounty;
  const nameChangeOk =
    endorsementType === "Name_Change_County"
      ? countyOk
      : endorsementType === "Name_Change_Principal"
        ? !!String(updatedName).trim()
        : endorsementType === "Name_Change_Deceased_Ward_Beneficiary"
          ? dwbOk
          : true;

  const tableColumns = [
    "bond_number",
    "bond_type",
    "bond_sub_category",
    "principals",
    "estate_name",
    "law_firm_name",
    "attorney_name",
    "bond_amount",
    "status",
    "created_at",
  ];

  return (
    <>
      <Navbar />
      <Box sx={{ padding: "20px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", maxWidth: "60vw" }}>
          <Typography variant="h4" fontWeight="bold">
            All Bonds
          </Typography>
          <span style={{ fontSize: "14px", color: "#666" }}>
           View all your active bonds below. You can also request a cancellation or endorsement directly from this page.
          </span>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              onClick={() => {
                const preservedLawFirm = filters.lawFirm;
                const cleared = {
                  bondNumber: [],
                  bondType: [],
                  bondSubCategory: [],
                  principal: [],
                  estate: [],
                  lawFirm: preservedLawFirm,
                  attorney: [],
                  status: [],
                  startDate: null,
                  endDate: null,
                };
                setFilters(cleared);
                setPage(0);
                dispatch(resetLawFirmBondsState());
                // ensure preset law firm is kept
                if (preservedLawFirm?.length) {
                  dispatch(setLawFirmPreset(preservedLawFirm));
                }
                fetchInitialData(cleared);
              }}
              startIcon={<RefreshIcon />}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
            <Button
              variant="contained"
              onClick={() => setFilterDialogOpen(true)}
              startIcon={<FilterListIcon />}
            >
              Filters
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                {tableColumns.map((col, i) => (
                  <TableCell
                    key={i}
                    sortDirection={orderBy === col ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === col}
                      direction={orderBy === col ? order : "asc"}
                      onClick={() => handleSort(col)}
                    >
                      {col
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ '& tr:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No Data Available
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((bond, index) => (
                  <TableRow key={index}>
                    <TableCell>{bond.bond_number || "Not Assigned"}</TableCell>
                    <TableCell>{bond.bond_type}</TableCell>
                    <TableCell>{bond.bond_sub_category}</TableCell>
                    <TableCell>{bond.principals.join(", ")}</TableCell>
                    <TableCell>{bond.estate_name}</TableCell>
                    <TableCell>{bond.law_firm_name}</TableCell>
                    <TableCell>{bond.attorney_name}</TableCell>
                    <TableCell>
                      ${Number(bond.bond_amount).toLocaleString()}
                    </TableCell>
                    <TableCell>{bond.status}</TableCell>
                    <TableCell>
                      {dayjs(bond.created_at).format("DD MMM YYYY")}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Bond Details">
                        <IconButton
                          onClick={() => {
                            const type = bond.bond_type
                              .toLowerCase()
                              .replace(" ", "-");
                            window.open(
                              `/vermost-${type}-bond-update/${bond.bond_id}`,
                              "_blank"
                            );
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel Bond">
                        <IconButton
                          color="error"
                          onClick={() => {
                            setSelectedBondToCancel(bond);
                            setCancellationFile(null);
                            setCancelDialogOpen(true);
                          }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Bond Endorsement">
                        <IconButton
                          color="secondary"
                          onClick={() => {
                            setSelectedBondToEndorse(bond);
                            setEndorsementType("Increase");
                            setNewBondAmount("");
                            setEndorsementFile(null);
                            setUpdatedName("");
                            setEndorsementToCounty("");
                            setEstimatedAssets("");
                            setDwbFirstName("");
                            setDwbMiddleName("");
                            setDwbLastName("");
                            setDwbSuffix("");
                            setDwbAlias("");
                            setEndorsementDialogOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            variant="body2"
            sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => setCantFindDialogOpen(true)}
          >
            Can't find a bond?
          </Typography>
          <TablePagination
          ActionsComponent={TablePaginationActions}
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={(e, p) => { setPage(p); dispatch(setLawFirmBondsPage(p)); }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); dispatch(setLawFirmBondsPage(0)); }}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{ m: 0 }}
          />
        </Box>

        <Dialog
          open={cancelDialogOpen}
          onClose={() => !isCancelling && setCancelDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: "1.3rem" }}>Bond Cancellation</DialogTitle>
          <DialogContent dividers>
            {selectedBondToCancel && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Cancellation Details
                </Typography>
                <Typography sx={{ mb: 1 }}>
                  <strong>Bond Number:</strong> {selectedBondToCancel.bond_number ?? "N/A"}
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  <strong>Bond Type:</strong> {selectedBondToCancel.bond_type}
                </Typography>

                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Documents
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                  Acceptable Documentation:
                </Typography>
                <ul style={{ paddingLeft: "24px", marginBottom: "20px", listStyleType: "disc" }}>
                  {getCancellationInstructionsList(selectedBondToCancel).map(
                    (item, idx) => (
                      <li
                        key={idx}
                        style={{ fontSize: "0.9rem", marginBottom: "6px" }}
                      >
                        {item}
                      </li>
                    )
                  )}
                </ul>

                <Button
                  variant="outlined"
                  component="label"
                  disabled={isCancelling}
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      const validTypes = [
                        "application/pdf",
                        "application/msword",
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                      ];
                      if (file && !validTypes.includes(file.type)) {
                        toast.error("Only PDF or Word documents are allowed.");
                        e.target.value = "";
                        return;
                      }
                      setCancellationFile(file);
                      toast.success("File selected successfully!");
                    }}
                  />
                </Button>
                {cancellationFile && (
                  <Typography variant="caption" sx={{ display: "block", mb: 2 }}>
                    Selected File: {cancellationFile.name}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ padding: 2 }}>
            <Button onClick={() => setCancelDialogOpen(false)} disabled={isCancelling}>
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={isCancelling || !cancellationFile}
              onClick={async () => {
                if (!selectedBondToCancel) return;
                if (!cancellationFile) {
                  toast.error("Please upload a document to cancel this bond.");
                  return;
                }
                setIsCancelling(true);
                const formData = new FormData();
                if (cancellationFile) {
                  formData.append("cancellation_file", cancellationFile);
                }
                try {
                  await apiClient(
                    `/cancel-bond/`,
                    "POST",
                    formData,
                    { bond_id: selectedBondToCancel.bond_id },
                    { "Content-Type": "multipart/form-data" }
                  );
                  toast.success("Bond cancelled successfully!");
                  setCancelDialogOpen(false);
                  await fetchInitialData();
                } catch (error) {
                  console.error(error);
                  toast.error(error.message || "Failed to cancel the bond.");
                } finally {
                  setIsCancelling(false);
                }
              }}
            >
              {isCancelling ? <CircularProgress size={24} color="inherit" /> : "Submit Cancellation"}
            </Button>
          </DialogActions>
        </Dialog>
        
        <Dialog
          open={cantFindDialogOpen}
          onClose={() => !cfSubmitting && setCantFindDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem' }}>
            Can't find a bond?
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Provide details below and our team will review and get back to you.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Bond Number"
                placeholder="e.g., PR-123456"
                value={cfBondNumber}
                onChange={(e) => setCfBondNumber(e.target.value)}
                fullWidth
              />

              <Autocomplete
                options={bondTypes}
                value={cfBondType}
                onChange={(e, v) => setCfBondType(v)}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>{option.label}</li>
                )}
                renderInput={(p) => <TextField {...p} label="Bond Type" />}
              />

              <TextField
                label="Name (optional)"
                placeholder="Estate / Ward / Trust / Beneficiary"
                value={cfName}
                onChange={(e) => setCfName(e.target.value)}
                fullWidth
              />

              <TextField
                label="Principal Name (optional)"
                value={cfPrincipalName}
                onChange={(e) => setCfPrincipalName(e.target.value)}
                fullWidth
              />

              <TextField
                label="Action you want to perform"
                select
                fullWidth
                value={cfAction}
                onChange={(e) => setCfAction(e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="cancellation">Cancellation</option>
                <option value="endorsement">Endorsement</option>
                <option value="other">Other</option>
              </TextField>

              <TextField
                label="Other Information"
                placeholder="Provide additional details"
                value={cfOtherInfo}
                onChange={(e) => setCfOtherInfo(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                helperText={cfAction === 'other' ? 'Required when action is Other' : 'Optional'}
                error={cfAction === 'other' && !cfOtherInfo.trim()}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCantFindDialogOpen(false)} disabled={cfSubmitting}>Close</Button>
            <Button
              variant="contained"
              disabled={cfSubmitting || (cfAction === 'other' && !cfOtherInfo.trim())}
              onClick={async () => {
                try {
                  setCfSubmitting(true);

                  const requesterName = `${userdetails?.first_name || ''} ${userdetails?.last_name || ''}`.trim();
                  const selectedFirmFromFilters = (filters?.lawFirm?.[0]?.label) || null;
                  const selectedFirmFromList = lawFirms.find((f) => f.value === lawfirmId)?.label;
                  const lawFirmName = selectedFirmFromFilters || selectedFirmFromList || '';

                  const niceAction = (cfAction || '').toString().trim();
                  const actionLabel = niceAction
                    ? niceAction.charAt(0).toUpperCase() + niceAction.slice(1)
                    : '';

                  const detailsTableHtml = `
<table style="width:100%; border-collapse:collapse; margin-top:12px; font-family:Arial, sans-serif; font-size:14px;">
  <thead>
    <tr>
      <th style="text-align:left; padding:8px 10px; border:1px solid #e5e7eb; background:#f9fafb; color:#111827;">Field</th>
      <th style="text-align:left; padding:8px 10px; border:1px solid #e5e7eb; background:#f9fafb; color:#111827;">Value</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Law Firm</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${lawFirmName || '-'}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Requester</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${requesterName || '-'}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Bond Number</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${(cfBondNumber || '').toString().trim() || '-'}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Bond Type</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${cfBondType?.label || '-'}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Name</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${(cfName || '').toString().trim() || '-'}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Principal Name</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${(cfPrincipalName || '').toString().trim() || '-'}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Requested Action</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${actionLabel || '-'}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Other Information</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${(cfOtherInfo || '').toString().trim() || '-'}</td>
    </tr>
  </tbody>
</table>`;

                  const contentHtml = `
<p style="font-family:Arial, sans-serif; font-size:14px; color:#111827;">A law firm user submitted a request from the Bond Archives page.</p>
<p style="font-family:Arial, sans-serif; font-size:14px; color:#111827; margin: 0 0 8px 0;"><strong>Law Firm:</strong> ${lawFirmName || '-'}</p>
<p style="font-family:Arial, sans-serif; font-size:14px; color:#111827; margin: 0 0 8px 0;"><strong>Requester:</strong> ${requesterName || '-'}</p>
<p style="font-family:Arial, sans-serif; font-size:14px; color:#111827;">Details:</p>
${detailsTableHtml}
`;

                  await apiClient(
                    `/send-portal-email/`,
                    "POST",
                    {
                      subject: `Request: Can't find a bond?` ,
                      content_html: contentHtml,
                    },
                    {},
                    { "Content-Type": "application/json" }
                  );

                  toast.success('Request submitted. Our team will review and get back to you.');
                  setCantFindDialogOpen(false);
                  setCfBondNumber('');
                  setCfBondType(null);
                  setCfName('');
                  setCfPrincipalName('');
                  setCfAction('cancellation');
                  setCfOtherInfo('');
                } catch (err) {
                  console.error('Email send failed:', err);
                  toast.error('Failed to submit the request. Please try again.');
                } finally {
                  setCfSubmitting(false);
                }
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Apply Filters</DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <Autocomplete
              multiple
              options={bondTypes}
              value={filters.bondType}
              onChange={(e, v) => setFilters((f) => ({ ...f, bondType: v }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Bond Type" />}
            />
            <Autocomplete
              multiple
              options={availableSubcategories}
              value={filters.bondSubCategory}
              onChange={(e, v) =>
                setFilters((f) => ({ ...f, bondSubCategory: v }))
              }
              renderInput={(p) => (
                <TextField {...p} label="Bond Sub Category" />
              )}
            />
            <Autocomplete
              multiple
              options={bondNumberOptions}
              value={filters.bondNumber}
              onChange={(e, v) => setFilters((f) => ({ ...f, bondNumber: v }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Bond Number" />}
            />
            <Autocomplete
              multiple
              options={principalFilterOptions}
              value={filters.principal}
              onChange={(e, v) => setFilters((f) => ({ ...f, principal: v }))}
              getOptionLabel={(o) => o?.label ?? ""}
              isOptionEqualToValue={(option, value) =>
                (option?.label ?? option?.value) === (value?.label ?? value?.value)
              }
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Principal" />}
            />
            <Autocomplete
              multiple
              options={estateFilterOptions}
              value={filters.estate}
              onChange={(e, v) => setFilters((f) => ({ ...f, estate: v }))}
              getOptionLabel={(o) => o?.label ?? ""}
              isOptionEqualToValue={(option, value) =>
                (option?.label ?? option?.value) === (value?.label ?? value?.value)
              }
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Estate / Ward / Trust / Beneficiary" />}
            />
            <Autocomplete
              multiple
              options={lawFirms}
              value={filters.lawFirm}
              onChange={(e, v) => setFilters((f) => ({ ...f, lawFirm: v }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Law Firm" />}
              disabled={true}
              sx={{ 
                '& .MuiInputBase-root': { 
                  backgroundColor: '#f5f5f5'
                }
              }}
            />
            <Autocomplete
              multiple
              options={attorneys}
              value={filters.attorney}
              onChange={(e, v) => setFilters((f) => ({ ...f, attorney: v }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Attorney" />}
            />
            <Autocomplete
              multiple
              options={statusOptions}
              value={filters.status}
              onChange={(e, v) => setFilters((f) => ({ ...f, status: v }))}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {option}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Status" />}
            />
            <TextField
              label="Start Date"
              type="date"
              value={filters.startDate || ""}
              onChange={(e) =>
                setFilters((f) => ({ ...f, startDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              value={filters.endDate || ""}
              onChange={(e) =>
                setFilters((f) => ({ ...f, endDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={clearFilters}>Clear Filters</Button>
            <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={applyFilters}>
              Apply Filters
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={endorsementDialogOpen}
          onClose={() =>
            !isSubmittingEndorsement && setEndorsementDialogOpen(false)
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1.3rem' }}>Bond Endorsement</DialogTitle>
          <DialogContent dividers>
            {selectedBondToEndorse?.bond_type === "Blanket" ? (
              <Typography variant="h6" sx={{ py: 5, textAlign: "center" }}>
                Endorsement not allowed for Blanket.
              </Typography>
            ) : selectedBondToEndorse ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Endorsement Details
                </Typography>
                <Typography sx={{ mb: 1 }}>
                  <strong>Bond Number:</strong> {selectedBondToEndorse.bond_number ?? "N/A"}
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  <strong>Bond Type:</strong> {selectedBondToEndorse.bond_type}
                </Typography>

                <TextField
                  label="Endorsement Type"
                  select
                  fullWidth
                  value={endorsementType}
                  onChange={(e) => setEndorsementType(e.target.value)}
                  SelectProps={{ native: true }}
                  required
                  sx={{ mb: 2 }}
                >
                  <option value="Increase">Increase</option>
                  <option value="Decrease">Decrease</option>
                  <option value="Name_Change_Principal">Name change (Principal)</option>
                  <option value="Name_Change_County">Name change (County)</option>
                  <option value="Name_Change_Deceased_Ward_Beneficiary">Name change (Deceased/Ward/Beneficiary)</option>
                </TextField>

                {isNameChangeEndorsement(endorsementType) ? (
                  endorsementType === "Name_Change_County" ? (
                    <Autocomplete
                      options={counties}
                      loading={loadingCounties}
                      value={
                        endorsementToCounty
                          ? counties.find((c) => c.value === endorsementToCounty) || null
                          : null
                      }
                      onChange={(e, selected) =>
                        setEndorsementToCounty(selected ? selected.value : "")
                      }
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.label
                      }
                      isOptionEqualToValue={(option, value) =>
                        option && value && option.value === value.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="To County"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingCounties ? (
                                  <CircularProgress color="inherit" size={18} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      fullWidth
                      disabled={loadingCounties}
                      sx={{ mb: 3 }}
                    />
                  ) : endorsementType === "Name_Change_Deceased_Ward_Beneficiary" ? (
                    (() => {
                      const labels = DWB_LABEL_MAP[selectedBondToEndorse?.bond_type] || DWB_LABEL_MAP.Probate;
                      if (labels.single) {
                        return (<TextField label={labels.prefix} value={updatedName} onChange={(e) => setUpdatedName(e.target.value)} fullWidth required sx={{ mb: 3 }} />);
                      }
                      return (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                          <Box sx={{ display: "flex", gap: 2 }}>
                            <TextField label={`${labels.prefix} First Name`} value={dwbFirstName} onChange={(e) => setDwbFirstName(e.target.value)} fullWidth required />
                            <TextField label={`${labels.prefix} Middle Name`} value={dwbMiddleName} onChange={(e) => setDwbMiddleName(e.target.value)} fullWidth />
                          </Box>
                          <Box sx={{ display: "flex", gap: 2 }}>
                            <TextField label={`${labels.prefix} Last Name`} value={dwbLastName} onChange={(e) => setDwbLastName(e.target.value)} fullWidth required />
                            <TextField label="Suffix" value={dwbSuffix} onChange={(e) => setDwbSuffix(e.target.value)} sx={{ maxWidth: 120 }} />
                          </Box>
                          <TextField label={labels.alias} value={dwbAlias} onChange={(e) => setDwbAlias(e.target.value)} fullWidth />
                        </Box>
                      );
                    })()
                  ) : (
                    <TextField
                      label="Updated Name"
                      name="updatedName"
                      value={updatedName}
                      onChange={(e) => setUpdatedName(e.target.value)}
                      fullWidth
                      required
                      sx={{ mb: 3 }}
                    />
                  )
                ) : (
                  <TextField
                    label="New Bond Amount (Rounded Up)"
                    name="newBondAmount"
                    value={newBondAmount}
                    onChange={(e) => setNewBondAmount(e.target.value)}
                    fullWidth
                    required
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                    }}
                    sx={{ mb: 3 }}
                  />
                )}

                {(selectedBondToEndorse.bond_type === "Probate" || selectedBondToEndorse.bond_type === "Guardianship") && (
                  <TextField
                    label="Updated Estimated Assets (optional)"
                    type="text"
                    inputMode="decimal"
                    value={estimatedAssets}
                    onChange={(e) => setEstimatedAssets(e.target.value)}
                    fullWidth
                    placeholder="e.g. 250000"
                    sx={{ mb: 2 }}
                    helperText="Fill the updated estimated assets value if additional assets were discovered."
                  />
                )}

                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Documents
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                  Acceptable Documentation:
                </Typography>
                <ul style={{ paddingLeft: "20px", marginBottom: "20px" }}>
                  {getEndorsementInstructions(selectedBondToEndorse).map(
                    (item, idx) => (
                      <li
                        key={idx}
                        style={{ fontSize: "0.9rem", marginBottom: "6px" }}
                      >
                        {item}
                      </li>
                    )
                  )}
                </ul>

                <Button
                  variant="outlined"
                  component="label"
                  disabled={isSubmittingEndorsement}
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Upload Supporting Document
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      const validTypes = [
                        "application/pdf",
                        "application/msword",
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                      ];
                      if (file && !validTypes.includes(file.type)) {
                        toast.error("Only PDF or Word documents are allowed.");
                        e.target.value = "";
                        return;
                      }
                      setEndorsementFile(file);
                      toast.success("File selected successfully!");
                    }}
                  />
                </Button>
                {endorsementFile && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Selected File: {endorsementFile.name}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Allowed file types: PDF, DOC, DOCX
                </Typography>

                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Contact Information
                </Typography>
                <TextField
                  label=" Name"
                  name="contactName"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  fullWidth
                  required
                  placeholder="Full name"
                  sx={{ mb: 2 }}
                />

                <TextField
                  label=" Email"
                  type="email"
                  name="contactEmail"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  fullWidth
                  required
                  placeholder="name@example.com"
                  sx={{ mb: 1 }}
                />
              </>
            ) : (
              <Typography variant="h6" sx={{ py: 5, textAlign: "center" }}>
                No bond selected.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEndorsementDialogOpen(false)}
              disabled={isSubmittingEndorsement}
            >
              Close
            </Button>
            {selectedBondToEndorse &&
              selectedBondToEndorse.bond_type !== "Blanket" && (
                <>
                  {(role === "internal_user" || role === "super_user") && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    disabled={
                      !selectedBondToEndorse ||
                      isSubmittingEndorsement ||
                      (isNameChangeEndorsement(endorsementType) && !nameChangeOk)
                    }
                    onClick={async () => {
                      if (!selectedBondToEndorse) return;
                      const getRiderUrl = () => {
                        switch (selectedBondToEndorse.bond_type) {
                          case "VA":
                            return `/release-rider-va/${selectedBondToEndorse.bond_id}`;
                          case "Trustee":
                            return `/release-rider-trustee/${selectedBondToEndorse.bond_id}`;
                          case "Probate":
                            return `/release-rider-probate/${selectedBondToEndorse.bond_id}`;
                          case "Blanket":
                            return `/release-rider-blanket/${selectedBondToEndorse.bond_id}`;
                          case "Guardianship":
                            return `/release-rider-guardianship/${selectedBondToEndorse.bond_id}`;
                          default:
                            return `/release-rider-guardianship/${selectedBondToEndorse.bond_id}`;
                        }
                      };
                      const isInternalOrSuper = role === "internal_user" || role === "super_user";
                      const hasDetails = endorsementType &&
                        contactName &&
                        contactEmail &&
                        (isNameChangeEndorsement(endorsementType) ? nameChangeOk : (!!newBondAmount && !!endorsementFile));

                      if (isInternalOrSuper && hasDetails) {
                        setIsSubmittingEndorsement(true);
                        const formData = new FormData();
                        formData.append("endorsement_type", endorsementType);
                        if (isNameChangeEndorsement(endorsementType)) {
                          if (endorsementType === "Name_Change_County") {
                            formData.append("updated_name", (endorsementToCounty || "").trim().split(/\s+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" "));
                          } else if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
                            if (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single) {
                              formData.append("updated_name", updatedName);
                            } else {
                              formData.append("updated_name", [dwbFirstName, dwbMiddleName, dwbLastName, dwbSuffix].filter(Boolean).join(" "));
                              formData.append("updated_first_name", dwbFirstName);
                              formData.append("updated_middle_name", dwbMiddleName);
                              formData.append("updated_last_name", dwbLastName);
                              formData.append("updated_suffix", dwbSuffix);
                              formData.append("updated_alias", dwbAlias);
                            }
                          } else {
                            formData.append("updated_name", updatedName);
                          }
                        } else {
                          formData.append("updated_bond_amount", newBondAmount.replace(/[^\d.]/g, ""));
                        }
                        if (endorsementFile) formData.append("endorsement_file", endorsementFile);
                        formData.append("contact_name", contactName);
                        formData.append("contact_email", contactEmail);
                        const estAssetsNum = estimatedAssets !== "" && !Number.isNaN(Number(estimatedAssets.replace(/[^\d.]/g, ""))) ? Number(estimatedAssets.replace(/[^\d.]/g, "")) : null;
                        if ((selectedBondToEndorse.bond_type === "Probate" || selectedBondToEndorse.bond_type === "Guardianship") && estAssetsNum != null) {
                          formData.append("estimated_assets", estAssetsNum);
                        }
                        try {
                          await apiClient(
                            `/endorse-bond/`,
                            "POST",
                            formData,
                            { bond_id: selectedBondToEndorse.bond_id },
                            { "Content-Type": "multipart/form-data" }
                          );
                          toast.success("Bond endorsed successfully!");
                          setEndorsementDialogOpen(false);
                          await fetchInitialData();
                          navigate(getRiderUrl());
                        } catch (error) {
                          console.error("Endorsement failed:", error);
                          toast.error(error.message || "Failed to endorse the bond.");
                        } finally {
                          setIsSubmittingEndorsement(false);
                        }
                      } else {
                        const url = getRiderUrl();
                        if (url) navigate(url);
                      }
                    }}
                  >
                    Release Rider
                  </Button>
                  )}
                  {role !== "internal_user" && role !== "super_user" && (
                  <Button
                    variant="contained"
                    disabled={
                      isSubmittingEndorsement ||
                      ((!isNameChangeEndorsement(endorsementType) && !endorsementFile)) ||
                      (isNameChangeEndorsement(endorsementType) && !nameChangeOk)
                    }
                    onClick={async () => {
                      if (
                        !selectedBondToEndorse ||
                        (!isNameChangeEndorsement(endorsementType) && !endorsementFile) ||
                      (isNameChangeEndorsement(endorsementType) ? !nameChangeOk : !newBondAmount) ||
                        !endorsementType ||
                        !contactName ||
                        !contactEmail
                      ) {
                        toast.error(
                          isNameChangeEndorsement(endorsementType)
                            ? "Please fill all required fields and contact info."
                            : "Please fill all required fields, contact info, and upload the file."
                        );
                        return;
                      }

                      setIsSubmittingEndorsement(true);

                      const formData = new FormData();
                      formData.append("endorsement_type", endorsementType);
                    if (isNameChangeEndorsement(endorsementType)) {
                      if (endorsementType === "Name_Change_County") {
                        formData.append("updated_name", (endorsementToCounty || "").trim().split(/\s+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" "));
                      } else if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
                        if (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single) {
                          formData.append("updated_name", updatedName);
                        } else {
                          formData.append("updated_name", [dwbFirstName, dwbMiddleName, dwbLastName, dwbSuffix].filter(Boolean).join(" "));
                          formData.append("updated_first_name", dwbFirstName);
                          formData.append("updated_middle_name", dwbMiddleName);
                          formData.append("updated_last_name", dwbLastName);
                          formData.append("updated_suffix", dwbSuffix);
                          formData.append("updated_alias", dwbAlias);
                        }
                      } else {
                        formData.append("updated_name", updatedName);
                      }
                    } else {
                      formData.append(
                        "updated_bond_amount",
                        newBondAmount.replace(/[^\d.]/g, "")
                      );
                    }
                      if (endorsementFile) formData.append("endorsement_file", endorsementFile);
                      formData.append("contact_name", contactName);
                      formData.append("contact_email", contactEmail);
                      const estAssetsNum = estimatedAssets !== "" && !Number.isNaN(Number(estimatedAssets.replace(/[^\d.]/g, ""))) ? Number(estimatedAssets.replace(/[^\d.]/g, "")) : null;
                      if ((selectedBondToEndorse.bond_type === "Probate" || selectedBondToEndorse.bond_type === "Guardianship") && estAssetsNum != null) {
                        formData.append("estimated_assets", estAssetsNum);
                      }

                      try {
                        await apiClient(
                          `/endorse-bond/`,
                          "POST",
                          formData,
                          { bond_id: selectedBondToEndorse.bond_id },
                          {
                            "Content-Type": "multipart/form-data",
                          }
                        );

                        toast.success("Bond endorsed successfully!");
                        setEndorsementDialogOpen(false);
                        await fetchInitialData();
                      } catch (error) {
                        console.error("Endorsement failed:", error);
                        toast.error(
                          error.message || "Failed to endorse the bond."
                        );
                      } finally {
                        setIsSubmittingEndorsement(false);
                      }
                    }}
                  >
                    {isSubmittingEndorsement ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Submit Endorsement"
                    )}
                  </Button>
                  )}
                </>
              )}
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default LawFirmApproveBonds;
